import { useState, useRef, useEffect, useCallback, forwardRef } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { LANGUAGES, getLanguageLabel } from "@/lib/routes";
import type { Language } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

interface Props {
  isTransparent: boolean;
  borderColor: string;
  textColor?: string;
  onOpenChange?: (open: boolean) => void;
}

const LanguageDropdown = forwardRef<HTMLDivElement, Props>(({ isTransparent, borderColor, textColor, onOpenChange }, forwardedRef) => {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  useEffect(() => {
    onOpenChange?.(open);
    if (open) haptics.selection();
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.visualViewport?.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.visualViewport?.removeEventListener("scroll", handleScroll);
    };
  }, [open]);

  const handleSelect = useCallback(
    (lang: Language) => {
      haptics.selection();
      setLanguage(lang);
      setOpen(false);
    },
    [setLanguage],
  );

  const triggerTextColor = textColor ?? (
    isTransparent
      ? "hsl(var(--foreground) / 0.94)"
      : "hsl(var(--foreground) / 0.90)"
  );

  return (
    <div ref={setRootRef} className="relative">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            className="liquid-capsule flex h-8 items-center gap-1 rounded-full border border-transparent px-2.5 text-xs font-semibold tracking-wide transition-all duration-token-base"
            style={{
              color: triggerTextColor,
              borderColor,
            }}
            aria-label={`${language.toUpperCase()} – ${getLanguageLabel(language, language)}`}
          >
            {language.toUpperCase()}
            <ChevronDown
              className="h-3 w-3 transition-transform duration-token-base"
              style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={{ top: 12, right: 16, bottom: 12, left: 16 }}
          className="liquid-panel z-[260] w-[10.25rem] max-h-[min(18rem,calc(100dvh-5.5rem))] overflow-y-auto rounded-xl p-1"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            triggerRef.current?.focus();
          }}
        >
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang}
              onSelect={() => handleSelect(lang)}
              className={cn(
                "rounded-lg px-3 py-2 text-[13px] font-medium",
                "data-[highlighted]:bg-accent/80 data-[highlighted]:text-foreground",
                lang === language
                  ? "bg-accent/85 text-foreground"
                  : "text-foreground/90 dark:text-foreground/76",
              )}
            >
              <span className="w-5 text-[11px] font-bold uppercase tracking-wider opacity-55">
                {lang}
              </span>
              <span className="flex-1">{getLanguageLabel(lang, language)}</span>
              {lang === language && <Check className="h-3.5 w-3.5 opacity-70" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});

LanguageDropdown.displayName = "LanguageDropdown";

export default LanguageDropdown;
